import React, { useState } from "react";
import { Card, Col, Container, NavLink, Row } from "react-bootstrap";
import { useSelector } from "react-redux";

// Import your dashboard icons (PNG/SVG)
import profileImg from "../assets/images/dahboard/profileicon.svg";
import passwordImg from "../assets/images/dahboard/passwordicon.svg";
import familyImg from "../assets/images/dahboard/membersicon.svg";
import membershipImg from "../assets/images/dahboard/membershipicon.svg";
import ticketImg from "../assets/images/dahboard/eventtickets.svg";
import transactionImg from "../assets/images/dahboard/transaction.svg";
import { useNavigate, Link, useLocation } from "react-router-dom";
import PaymentStatusModal from "../components/common/PaymentStatusModal";
import { FaAngleRight } from "react-icons/fa6";
import qrcodeImg from "../assets/images/icons/qrcode.svg";
import MemberCardModal from "../components/member/MemberCardModal";
import { SET_LOADING } from "../redux/user";
import { api_paths } from "../config/api.paths";
import API from "../config/axios";
import { useDispatch } from "react-redux";
import EventCard from "../components/events/EventCard";
import AddProfilePhotoModal from "../components/common/AddProfilePhotoModal";
import ProfilePhotoCropper from "../components/member/ProfilePhotoCropper";
import { toastErrorMessage, toastSuccessMessage } from "../utils/utils";
import { SET_USER } from "../redux/user";

const Dashboarddetails = [
  {
    id: 1,
    title: "My Profile",
    text: "View and update your personal information, contact details, and prefrences.",
    buttonLabel: "Go to Profile",
    img: profileImg,
    path: "/profile",
  },
  {
    id: 2,
    title: "Change Password",
    text: "Update your account password securely to keep your account safe.",
    buttonLabel: "Update Security",
    img: passwordImg,
    path: "/change-password",
  },
  {
    id: 3,
    title: "Family Members",
    text: "Manage your family members, and new dependents and update their info.",
    buttonLabel: "Manage Family",
    img: familyImg,
    path: "/family-member",
  },
  {
    id: 4,
    title: "Membership",
    text: "View your current membership status, renew plans, and access membership history.",
    buttonLabel: "View Details",
    img: membershipImg,
    path: "/membership",
  },
  {
    id: 5,
    title: "Transactions",
    text: "View your transaction history and download receipts for your purchases.",
    buttonLabel: "View History",
    img: transactionImg,
    path: "/transactions",
  },
  {
    id: 6,
    title: "Event Tickets",
    text: "Access your purchased tickets for upcoming community events and gatherings.",
    buttonLabel: "My Tickets",
    img: ticketImg,
    path: "/event-ticket",
  },
];

function DashboardCard({ item }) {
  const navigate = useNavigate();
  const { title, text, img, path, buttonLabel } = item;

  const handleClick = () => {
    if (path) navigate(path);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <Card
      className="dashboard-card border-0"
      role="button"
      tabIndex={0}
      // onClick={handleClick}
      // onKeyDown={handleKeyDown}
      aria-label={title}
    >
      <Card.Body className="d-flex gap-4 flex-column p-xl-4 p-lg-2 p-md-2 border-0">
        <div className="icon-wrap">
          <img src={img} alt={title} className="dashboard-card-icon" />
        </div>
        <div>
          <Card.Title className="mb-1 fw-semibold f-16">{title}</Card.Title>
          <Card.Text className="fw-normal mb-0 f-14">{text}</Card.Text>
          <div className="mt-3">
            <button
              className="btn btn-orange light-orange-bg d-flex align-items-center gap-2 rounded-5 px-3"
              onClick={handleClick}
              onKeyDown={handleKeyDown}
            >
              <span className="profile-btn-text">{buttonLabel}</span>
              <span className="profile-btn-icon ms-2">
                <FaAngleRight size={12} className="right-icon" />
              </span>
            </button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useSelector((state) => state.USER_DATA);
  const { loading } = useSelector((state) => state.USER_DATA);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [paymentStatus, setPaymentStatus] = React.useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const [showAddPhotoPrompt, setShowAddPhotoPrompt] = useState(false);
  // const [hasPrompted, setHasPrompted] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [savingPhoto, setSavingPhoto] = useState(false);
  const [showMemberCard, setShowMemberCard] = useState(false);
  const fileInputRef = React.useRef(null);
  const IMAGE_BASEURL = process.env.REACT_APP_IMAGE_BASEURL || "";

  const fetchUser = async () => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.get(api_paths.member.GET_USER);
      if (response.status === 200) {
        const data = response.data?.payload?.result;
        dispatch(SET_USER(data));
      }
    } catch (error) {
      console.log("Error fetching user:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const uploadProfilePhoto = async (file) => {
    if (!file) return;
    try {
      setSavingPhoto(true);
      dispatch(SET_LOADING(true));

      const formData = new FormData();
      formData.append("profile_picture", file);

      await API.put(api_paths.member.EDIT_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toastSuccessMessage("Profile photo updated");
      await fetchUser();

      setSelectedImage(null);
      setShowPhotoModal(false);
    } catch (error) {
      toastErrorMessage(
        error.response?.data?.message || "Failed to update profile photo",
      );
    } finally {
      setSavingPhoto(false);
      dispatch(SET_LOADING(false));
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.get(
        `${api_paths.member.EVENTS.EVENT_PAGE.GET_LIST}/all?page=1&limit=3&descending=true`,
      );
      const data = response.data?.payload?.result?.data || [];
      setUpcomingEvents(data.slice(0, 3));
    } catch (error) {
      console.error("Error fetching upcoming events:", error);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  React.useEffect(() => {
    fetchUpcomingEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const profileImage = React.useMemo(() => {
    const raw =
      user?.profile_picture || user?.profile_picture_path || user?.image || "";
    if (!raw) return null;
    if (raw.startsWith("http")) return raw;
    return `${IMAGE_BASEURL}${raw}`;
  }, [user, IMAGE_BASEURL]);

  React.useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const lastPromptDate = localStorage.getItem("last_profile_photo_prompt");

    if (user && !profileImage && !loading && lastPromptDate !== today) {
      setShowAddPhotoPrompt(true);
      localStorage.setItem("last_profile_photo_prompt", today);
    }
  }, [user, profileImage, loading]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("status");
    const autoLogin = params.get("auto_login") === "true";
    const convergeId = params.get("converge_transaction_id");

    if (status === "success" && autoLogin) {
      setPaymentStatus({ status, convergeId });
    }
  }, [location]);

  const handleCloseModal = () => {
    setPaymentStatus(null);
    navigate("/dashboard", { replace: true });
  };
  const handleRedirect = () => {
    setPaymentStatus(null);
    // Remove query params from URL without reloading
    navigate("/transactions", { replace: true });
  };

  const displayName =
    `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Guest";
  return (
    <>
      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus?.status}
          convergeId={paymentStatus?.convergeId}
          onClose={handleCloseModal}
          onNavigate={handleRedirect}
        />
      )}
      <Container>
        <Row className="my-4 mx-auto align-items-center">
          <Col
            lg={12}
            className="d-flex justify-content-between align-items-start align-items-sm-center gap-2"
          >
            <div>
              <h1 className="mb-2 fw-bold text-main f-20">
                Welcome, <span className="orange">{displayName}</span>
              </h1>
              <p className="mb-0 text-muted f-14">
                Access your membership details, events, and account settings
                below.
              </p>
            </div>
            {user?.membership_id && (
              <button
                className="qrcode-icon-btn flex-shrink-0 mt-1 mt-sm-0"
                onClick={() => setShowMemberCard(true)}
                title="View Membership Card"
              >
                <img
                  src={qrcodeImg}
                  alt="QR Code"
                  className="qrcode-icon-img"
                />
              </button>
            )}
          </Col>
        </Row>

        <Row className="g-4 mb-5 ">
          {Dashboarddetails.map((b) => (
            <Col
              key={b.id}
              lg={4}
              md={6}
              sm={12}
              xs={12}
              className="dash-card-sections"
            >
              <DashboardCard item={b} />
            </Col>
          ))}
        </Row>
        {/* events */}
        <Row className="my-5">
          <Col lg={12}>
            <div className="d-flex justify-content-md-between text-start flex-column flex-md-row">
              <div className="d-flex flex-column home-headers">
                <h2 className="text-main mb-2 f-20 fw-bold">Upcoming Events</h2>
                <p className="mb-0">
                  Don't miss RSVP if you would like to attend the event
                </p>
              </div>
              <NavLink
                as={Link}
                to="/events"
                className="view-all-link ms-md-auto mt-3 mt-sm-0"
              >
                View All
              </NavLink>
            </div>
          </Col>
        </Row>
        <Row className="g-4 mb-5">
          {loading ? (
            [...Array(3)].map((_, i) => <EventCard key={i} isLoading={true} />)
          ) : upcomingEvents?.length > 0 ? (
            upcomingEvents.map((event) => (
              <EventCard
                key={event._id}
                item={event}
                isLoading={false}
                isMember={user?.is_membership_active}
              />
            ))
          ) : (
            <Col lg={12} className="text-center py-5">
              <p className="text-muted">No upcoming events found.</p>
            </Col>
          )}
        </Row>
      </Container>
      <AddProfilePhotoModal
        show={showAddPhotoPrompt}
        onHide={() => setShowAddPhotoPrompt(false)}
        onPickPhoto={() => {
          setShowAddPhotoPrompt(false);
          fileInputRef.current?.click();
        }}
      />
      <ProfilePhotoCropper
        show={showPhotoModal}
        onHide={() => {
          setSelectedImage(null);
          setShowPhotoModal(false);
        }}
        onSave={uploadProfilePhoto}
        initialImage={selectedImage || profileImage}
        saving={savingPhoto}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (!file) return;

          const reader = new FileReader();
          reader.onload = (ev) => {
            setSelectedImage(ev.target.result);
            setShowPhotoModal(true);
          };
          reader.readAsDataURL(file);

          e.target.value = "";
        }}
      />
      <MemberCardModal
        show={showMemberCard}
        onHide={() => setShowMemberCard(false)}
        user={user}
      />
    </>
  );
}
